import { BasicFormState } from 'hooks/useForm';
import { IAddress } from 'utils/address';
import useZipCodeAreas from 'hooks/useZipCodeAreas';
import { ZipCodeArea } from 'api/model';
import { useMemo } from 'react';
import { AssignmentField } from './types';

const convertToIAddress = (
  name: string,
  address: string,
  city: string,
  zip: string,
  county?: ZipCodeArea
): IAddress | undefined => {
  // if any of the key fields are empty, return undefined
  if (!address || !city || !zip) {
    return undefined;
  }

  return {
    name,
    address,
    city,
    zip,
    county,
  };
};

const useGetAssignmentAddresses = (state: BasicFormState<AssignmentField>) => {
  const { allZipCodeAreas } = useZipCodeAreas();
  const result = useMemo(() => {
    // get the from address
    const fromAddress = convertToIAddress(
      state[AssignmentField.fromName],
      state[AssignmentField.fromAddress],
      state[AssignmentField.fromCity],
      state[AssignmentField.fromZip],
      allZipCodeAreas.find(
        (area) =>
          !!state[AssignmentField.fromCounty] &&
          area.id === Number(state[AssignmentField.fromCounty])
      )
    );
    // get the stopover address
    const stopOverAddress = convertToIAddress(
      state[AssignmentField.stopOverName],
      state[AssignmentField.stopOverAddress],
      state[AssignmentField.stopOverCity],
      state[AssignmentField.stopOverZip],
      allZipCodeAreas.find(
        (area) =>
          !!state[AssignmentField.stopOverCounty] &&
          area.id === Number(state[AssignmentField.stopOverCounty])
      )
    );
    // get the to address
    const toAddress = convertToIAddress(
      state[AssignmentField.toName],
      state[AssignmentField.toAddress],
      state[AssignmentField.toCity],
      state[AssignmentField.toZip],
      allZipCodeAreas.find(
        (area) =>
          !!state[AssignmentField.toCounty] &&
          area.id === Number(state[AssignmentField.toCounty])
      )
    );

    const personalTransportToAssignmentStartStartAddress = convertToIAddress(
      state[AssignmentField.personalTransportationToAssignmentName],
      state[AssignmentField.personalTransportationToAssignmentAddress],
      state[AssignmentField.personalTransportationToAssignmentCity],
      state[AssignmentField.personalTransportationToAssignmentZip],
      allZipCodeAreas.find(
        (area) =>
          !!state[AssignmentField.personalTransportationToAssignmentCounty] &&
          area.id ===
            Number(
              state[AssignmentField.personalTransportationToAssignmentCounty]
            )
      )
    );

    const addresses = [];
    const fromToTo = [];
    const fromToStopOver = [];
    const stopOverToEnd = [];
    const personalTransportToAssignmentStart = [];
    if (personalTransportToAssignmentStartStartAddress) {
      addresses.push(personalTransportToAssignmentStartStartAddress);
      personalTransportToAssignmentStart.push(
        personalTransportToAssignmentStartStartAddress
      );
    }
    if (fromAddress) {
      addresses.push(fromAddress);
      fromToStopOver.push(fromAddress);
      fromToTo.push(fromAddress);
      personalTransportToAssignmentStart.push(fromAddress);
    }
    if (stopOverAddress) {
      addresses.push(stopOverAddress);
      fromToStopOver.push(stopOverAddress);
      stopOverToEnd.push(stopOverAddress);
    }
    if (toAddress) {
      addresses.push(toAddress);
      stopOverToEnd.push(toAddress);
      fromToTo.push(toAddress);
    }
    return {
      addresses,
      startAddress: fromAddress,
      stopOverAddress,
      endAddress: toAddress,
      fromToTo,
      fromToStopOver,
      stopOverToEnd,
      personalTransportToAssignmentStart,
    };
  }, [allZipCodeAreas, state]);
  return result;
};

export default useGetAssignmentAddresses;
